<template>
  <div id="whale-backtester-market-timing-tab">
    <helpers-section-header class="mt-0 mb-6"> Market Timing Strategies </helpers-section-header>
    <p>
      Market timing lets you automatically shift your portfolio to a more defensive posture when
      specific market conditions are detected. When a sell trigger fires, the system can move a
      portion of your portfolio to cash, halt new buys, open a short hedge, or switch to an
      entirely different trading model — then resume normal trading once conditions improve.
    </p>
    <p>
      Each market timing rule combines a <strong>sell trigger</strong> (what causes defensive
      action) with one or more <strong>resume triggers</strong> (what signals that conditions have
      normalized). You can add multiple rules and require either <em>all</em> of them to fire
      simultaneously (AND) or allow <em>any one</em> to trigger independently (OR).
    </p>
    <v-container fluid pa-0>
      <v-checkbox
        v-model="criteria.market_timing.enabled"
        label="Enable Market Timing"
        density="compact"
        hide-details
      />
      <helpers-section-sub-header>
        % of Long Portfolio to Move to Cash (0–100)
      </helpers-section-sub-header>
      <p class="text-body-2 mb-2">
        When the sell trigger fires, this percentage of your long holdings will be sold and held as
        cash. Set to 100 to go fully defensive, or a lower value (e.g. 50) to reduce exposure
        while staying partially invested.
      </p>
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="criteria.market_timing.percent_of_portfolio_to_cash"
            label="% of Portfolio to Cash"
            variant="outlined"
            density="compact"
            style="max-width: 200px"
            hint="0 = no liquidation, 100 = fully move to cash"
            persistent-hint
          />
        </v-col>
      </v-row>
      <helpers-section-sub-header>
        Freeze New Buys While Sell Signal Is Active
      </helpers-section-sub-header>
      <p class="text-body-2 mb-2">
        When checked, no new positions will be opened while the market timing rule is in its
        defensive state, even if buy signals are generated. Useful when combined with a partial
        cash conversion above.
      </p>
      <v-row>
        <v-col cols="12" md="6">
          <v-checkbox
            v-model="criteria.market_timing.freeze_new_buys"
            label="Prevent New Buys During Defensive Period"
            color="primary"
            hide-details
            density="compact"
            class="mt-0"
          />
        </v-col>
      </v-row>
      <helpers-section-sub-header>
        Hedge Portfolio via Short Index Position
      </helpers-section-sub-header>
      <p class="text-body-2 mb-2">
        Opens a short position in a broad index ETF when the sell trigger fires, providing a
        partial offset to your remaining long holdings. For example, shorting 50% of your long
        value in SPY creates a net-neutral market exposure. The hedge is closed when the resume
        trigger fires.
      </p>
      <v-row>
        <v-col cols="12" md="3">
          <v-checkbox
            v-model="criteria.market_timing.hedge_enabled"
            label="Enable Short Hedge"
            color="primary"
            hide-details
            density="compact"
            class="mt-0"
          />
        </v-col>
        <v-col v-if="criteria.market_timing.hedge_enabled" cols="12" md="3">
          <v-text-field
            v-model="criteria.market_timing.hedge_short_percentage"
            label="% of Portfolio"
            variant="outlined"
            density="compact"
            style="max-width: 200px"
            hint="% of long value to use for short index position."
            persistent-hint
          />
        </v-col>
        <v-col v-if="criteria.market_timing.hedge_enabled" cols="12" md="3">
          <v-select
            v-model="criteria.market_timing.hedge_index"
            style="max-width: 300px"
            :items="indices"
            item-title="text"
            variant="outlined"
            density="compact"
            hide-details
            label="Choose Index to short"
          />
        </v-col>
      </v-row>
      <helpers-section-sub-header>
        Switch to a Different Trading Strategy When Defensive
      </helpers-section-sub-header>
      <p class="text-body-2 mb-2">
        Instead of (or in addition to) moving to cash, you can pivot to an entirely different
        trading model when the sell trigger fires — for example, switching from a growth strategy
        to a fixed-income or inverse-ETF model during downturns. The selected model's signals,
        filters, entry rules, risk management, and exit rules all take effect.
      </p>
      <v-row>
        <v-col cols="12" md="3">
          <v-checkbox
            v-model="criteria.market_timing.use_model"
            label="Enable Changing Strategies"
            color="primary"
            hide-details
            density="compact"
            class="mt-0"
          />
        </v-col>
        <v-col v-if="criteria.market_timing.use_model" cols="12" md="3">
          <v-select
            v-model="criteria.market_timing.model"
            style="max-width: 300px"
            :items="tradingModels"
            variant="outlined"
            item-title="description"
            item-value="id"
            density="compact"
            hide-details
            label="Choose Trading Model to Use"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-checkbox
            v-if="criteria.market_timing.use_model"
            v-model="criteria.market_timing.clear_when_switching_models"
            label="Exit Existing Holdings When Changing Strategies"
            color="primary"
            hide-details
            density="compact"
            class="mt-0"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <helpers-section-sub-header class="mb-2">
            Sell Trigger Rules
          </helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Add one or more sell trigger rules below. When a rule fires, your portfolio will shift
            to the defensive posture configured above. Each rule also has its own resume triggers
            that define when to return to normal trading.
          </p>
          <v-radio-group v-model="criteria.market_timing.and_or_indicators" row hide-details>
            <v-radio
              label="AND — all enabled rules must fire simultaneously to trigger"
              value="and"
              color="primary"
              hint=""
              persistent-hint
            />
            <v-radio
              label="OR — any single enabled rule firing will trigger"
              value="or"
              color="primary"
              hint=""
              persistent-hint
            />
          </v-radio-group>
        </v-col>

        <v-data-table
          :headers="mt_headers"
          :items="criteria.market_timing_rules"
          hide-default-footer
          items-per-page="-1"
          class="mt-1 mb-1"
        >
          <template #top>
            <v-dialog v-model="mtDialog" max-width="600px" scrollable>
              <template #activator="{ props }">
                <v-btn color="primary" style="max-width: 350px" class="mb-4" v-bind="props">
                  Add Market Timing Rule
                </v-btn>
              </template>
              <v-card>
                <v-card-title>
                  <span class="text-h5">{{ MtTitle }}</span>
                </v-card-title>

                <v-card-text style="height: 400px">
                  <v-container>
                    <v-row density="compact">
                      <v-col cols="12" sm="12">
                        <v-checkbox
                          v-model="editedMt.enabled"
                          density="compact"
                          label="Rule Enabled"
                        />
                      </v-col>
                      <v-col cols="12">
                        <h3 class="text-h5">What triggers the sell rule?</h3>
                        <v-select
                          v-model="editedMt.sell_trigger"
                          :items="sell_triggers"
                          label="Choose a Sell Trigger"
                          item-value="id"
                          item-title="description"
                          hide-details
                          single-line
                          variant="outlined"
                          density="compact"
                          @update:model-value="clear_resumes()"
                        />
                      </v-col>
                      <v-col v-if="editedMt.sell_trigger == 1" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when a broad market index falls below its own moving average —
                          a classic trend-following signal. The 200-day MA on the S&amp;P 500 is
                          widely watched as a bull/bear dividing line. Leave the optional % field
                          blank to trigger exactly at the crossover, or enter a value (e.g. 2) to
                          require the index to be at least 2% below the MA before triggering.
                        </p>
                        <v-text-field
                          v-model="editedMt.moving_average.days"
                          style="max-width: 200px"
                          type="number"
                          variant="outlined"
                          density="compact"
                          label="Moving Average Days"
                          hint="e.g. 200 for the 200-day MA"
                          persistent-hint
                        />
                        <v-select
                          v-model="editedMt.moving_average.index"
                          style="max-width: 300px"
                          class="mt-3"
                          :items="indices"
                          item-title="text"
                          variant="outlined"
                          density="compact"
                          hide-details
                          label="Index to Monitor"
                        />
                        <v-text-field
                          v-model="editedMt.moving_average.percent"
                          style="max-width: 250px"
                          class="mt-3"
                          type="number"
                          variant="outlined"
                          density="compact"
                          label="Optional: % Below MA to Require"
                          hint="Leave blank to trigger at the crossover. Enter e.g. 2 to require the index to be 2% below the MA."
                          persistent-hint
                        />
                      </v-col>
                      <v-col v-if="editedMt.sell_trigger == 2" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when an index has risen an unusually large amount relative to a
                          moving average — a potential sign of an overheated market. You can compare
                          the index price to a single MA (e.g. S&amp;P 500 is 10% above its 200-day
                          MA), or compare one MA to a second, longer MA (e.g. 50-day MA is 5%
                          above the 200-day MA). Both approaches detect extended markets that may
                          be due for a pullback.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.index_performance.index"
                              style="max-width: 300px"
                              class="mb-2"
                              :items="indices"
                              item-title="text"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="Index to Monitor"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.index_performance.days"
                              style="max-width: 200px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Moving Average Days (MA1)"
                              hint="e.g. 50 or 200"
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="editedMt.index_performance.compare_to"
                              style="max-width: 300px"
                              :items="['price', 'moving average']"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="Compare MA1 to..."
                            />
                          </v-col>
                          <v-col
                            v-if="editedMt.index_performance.compare_to == 'moving average'"
                            cols="12"
                            sm="6"
                          >
                            <v-text-field
                              v-model="editedMt.index_performance.compare_to_ma"
                              style="max-width: 200px"
                              type="number"
                              label="MA2 Days"
                              variant="outlined"
                              density="compact"
                              hint="Second (longer) moving average, e.g. 200"
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.index_performance.percent_increase"
                              style="max-width: 300px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="% MA1 is Above Comparison"
                              hint="Trigger when MA1 exceeds this % above the price or MA2"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col v-if="editedMt.sell_trigger == 3" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers based on a macro-economic or sentiment indicator crossing a
                          threshold. Available indicators include the CBOE VIX (market fear gauge),
                          the Fed Funds Rate, the Buffett Indicator (total market cap vs. GDP),
                          the 10-Year Treasury Rate, and the WhaleWisdom insider sell/buy ratio.
                          You can also trigger on one index outperforming or underperforming
                          another by a given percentage over a specified period.
                        </p>
                        <v-select
                          v-model="editedMt.macro_indicator.macro_data_type_id"
                          variant="outlined"
                          density="compact"
                          :items="macro_indicators"
                          label="Choose Macro Indicator"
                          item-value="macro_data_type_id"
                          item-title="description"
                          class="shrink"
                        />
                        <div
                          v-if="
                            editedMt.macro_indicator.macro_data_type_id &&
                            editedMt.macro_indicator.macro_data_type_id != 'outperformed' &&
                            editedMt.macro_indicator.macro_data_type_id != 'underperformed'
                          "
                        >
                          <v-select
                            v-model="editedMt.macro_indicator.above_below"
                            variant="outlined"
                            density="compact"
                            :items="above_below"
                            label="Above/Below"
                            class="shrink"
                          />

                          <v-text-field
                            v-if="
                              editedMt.macro_indicator.above_below == 'above' ||
                              editedMt.macro_indicator.above_below == 'below'
                            "
                            v-model="editedMt.macro_indicator.threshold"
                            variant="outlined"
                            style="max-width: 300px"
                            density="compact"
                            label="Threshold"
                          />
                          <v-select
                            v-else
                            v-model="editedMt.macro_indicator.previous_reading"
                            style="max-width: 300px"
                            :items="[
                              '1 day ago',
                              '1 week ago',
                              '1 month ago',
                              '3 months ago',
                              '6 months ago',
                              '1 year ago',
                              'last changed reading'
                            ]"
                            hide-details
                            variant="outlined"
                            density="compact"
                            label="Previous Reading to Use"
                          />
                        </div>
                      </v-col>
                      <v-col
                        v-if="
                          editedMt.macro_indicator.macro_data_type_id == 'outperformed' ||
                          editedMt.macro_indicator.macro_data_type_id == 'underperformed'
                        "
                        cols="12"
                        class="mt-2 pl-4"
                      >
                        <v-row density="compact">
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.macro_indicator.index1"
                              style="max-width: 300px"
                              :items="indices"
                              item-title="text"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="Choose Index 1"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.macro_indicator.index2"
                              style="max-width: 300px"
                              :items="indices"
                              item-title="text"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="Choose Index 2"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="editedMt.macro_indicator.threshold"
                              variant="outlined"
                              density="compact"
                              style="max-width: 300px"
                              label="Percentage Points Difference"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.macro_indicator.days"
                              density="compact"
                              hide-details
                              variant="outlined"
                              :items="stocks_vs_sp_months"
                              style="max-width: 300px"
                              item-title="text"
                              item-value="value"
                              label="# Days Out/Under Performed"
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col
                        v-if="
                          editedMt.sell_trigger == 3 &&
                          editedMt.macro_indicator.macro_data_type_id == 8
                        "
                        class="mt-2 pl-4"
                        cols="12"
                      >
                        <v-checkbox
                          v-model="editedMt.macro_indicator.ma_enabled"
                          label="Use Moving Average"
                          color="primary"
                          hide-details
                          density="compact"
                          class="mt-0"
                        />

                        <v-text-field
                          v-model="editedMt.macro_indicator.madays"
                          variant="outlined"
                          density="compact"
                          label="Moving Average Days"
                        />
                      </v-col>
                      <v-col v-if="editedMt.sell_trigger == 4" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when a market index makes a new N-day closing low — a sign of
                          sustained selling pressure. For example, a 52-day (roughly quarterly) low
                          on the S&amp;P 500 indicates the market is at its weakest point in three
                          months. Shorter windows (e.g. 20 days) are more sensitive; longer windows
                          (e.g. 200 days) only trigger during significant downtrends.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" md="12">
                            <v-select
                              v-model="editedMt.day_low_high_index"
                              style="max-width: 300px"
                              :items="indices"
                              item-title="text"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="Index to Monitor"
                            />
                          </v-col>
                          <v-col>
                            <v-text-field
                              v-model="editedMt.day_low_high"
                              style="max-width: 200px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="N-Day Low Window"
                              hint="Trigger when the index hits a new low over this many days"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col v-if="editedMt.sell_trigger == 5" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when a market index makes a new N-day closing high — a potential
                          signal of an overbought, euphoric market near a peak. For example, if
                          the S&amp;P 500 hits a new 252-day (annual) high, some models treat this
                          as a contrarian warning. Combine with other indicators to avoid exiting
                          during healthy uptrends.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" md="12">
                            <v-select
                              v-model="editedMt.day_low_high_index"
                              style="max-width: 300px"
                              :items="indices"
                              item-title="text"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="Index to Monitor"
                            />
                          </v-col>
                          <v-col>
                            <v-text-field
                              v-model="editedMt.day_low_high"
                              style="max-width: 200px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="N-Day High Window"
                              hint="Trigger when the index hits a new high over this many days"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col v-if="editedMt.sell_trigger == 6" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers on a moving average crossover — one of the most widely used
                          technical signals. Set MA1 (shorter) <em>under</em> MA2 (longer) for a
                          <strong>Death Cross</strong> (bearish — e.g. 50-day crosses below
                          200-day). Set MA1 <em>over</em> MA2 for a <strong>Golden Cross</strong>
                          (bullish confirmation — often used as a resume trigger instead of a sell
                          trigger). The classic Death Cross is 50-day under 200-day on the S&amp;P 500.
                        </p>
                        <v-row>
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.moving_average.index"
                              style="max-width: 300px"
                              :items="indices"
                              item-title="text"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Index to Monitor"
                            />
                          </v-col>

                          <v-col cols="12">
                            <v-text-field
                              v-model="editedMt.moving_average.days"
                              style="max-width: 200px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="MA1 Days (shorter)"
                              hint="e.g. 50 for the 50-day MA"
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.moving_average.over_under"
                              style="max-width: 300px"
                              :items="['over', 'under']"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="MA1 is Over / Under MA2"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="editedMt.moving_average.days2"
                              style="max-width: 200px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="MA2 Days (longer)"
                              hint="e.g. 200 for the 200-day MA"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col
                        v-if="editedMt.sell_trigger == 7 || editedMt.sell_trigger == 8"
                        class="mt-2 pl-4"
                        cols="12"
                      >
                        <p v-if="editedMt.sell_trigger == 7" class="text-body-2 mb-3">
                          Triggers when a high percentage of all stocks in the universe are trading
                          above a chosen moving average — a <strong>market breadth</strong> signal
                          indicating an overbought or euphoric market. For example, when 80% of
                          stocks are above their 200-day MA, the market may be over-extended and
                          vulnerable to a correction.
                        </p>
                        <p v-else class="text-body-2 mb-3">
                          Triggers when a high percentage of all stocks in the universe are trading
                          below a chosen moving average — a classic <strong>breadth deterioration</strong>
                          signal. When 70% or more of stocks fall below their 200-day MA, broad
                          market conditions are typically unfavorable. This is one of the most
                          reliable warning signs of a sustained downturn.
                        </p>
                        <v-select
                          v-model="editedMt.moving_average.days"
                          style="max-width: 200px"
                          :items="ma_days"
                          label="Moving Average Days"
                          hint="Typically 50, 100, or 200 days"
                          density="compact"
                          persistent-hint
                          variant="outlined"
                        />
                        <v-text-field
                          v-model="editedMt.moving_average.percent"
                          style="max-width: 250px"
                          class="mt-3"
                          type="number"
                          variant="outlined"
                          density="compact"
                          persistent-hint
                          label="% of Stocks Threshold"
                          :hint="
                            'Trigger when at least this % of stocks are ' +
                            (editedMt.sell_trigger == 7 ? 'above' : 'below') +
                            ' the moving average'
                          "
                        />
                      </v-col>

                      <v-col v-if="editedMt.sell_trigger == 10" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers based on the moving averages of your own portfolio's equity
                          curve — useful for detecting when your strategy's own momentum is turning
                          negative, regardless of overall market conditions. For example, trigger
                          when your portfolio's 20-day equity MA falls below its 60-day MA, or
                          when the current equity falls a specified % below a moving average.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.portfolio_equity.days"
                              style="max-width: 200px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Moving Average Days 1"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="editedMt.portfolio_equity.compare_to"
                              style="max-width: 300px"
                              :items="['is above', 'is below']"
                              hide-details
                              variant="outlined"
                              density="compact"
                              label="How to compare"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.portfolio_equity.compare_to_ma"
                              style="max-width: 200px"
                              type="number"
                              label="Moving Average Days 2"
                              variant="outlined"
                              density="compact"
                              hint=""
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.portfolio_equity.percent_increase"
                              style="max-width: 300px"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="% difference vs 1st MA (optional)"
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col v-if="editedMt.sell_trigger == 11" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when your WhaleTrader portfolio equity (or a chosen index) has
                          declined by a specified percentage over a given number of days. This is a
                          direct drawdown guard — for example, trigger if your portfolio has fallen
                          10% over the past 60 days. Select "WhaleTrader Portfolio Equity" to
                          protect your own portfolio, or choose a market index to use as a proxy.
                        </p>
                        <v-select
                          v-model="editedMt.portfolio_equity.index"
                          style="max-width: 300px"
                          :items="indices"
                          item-title="text"
                          variant="outlined"
                          density="compact"
                          label="Portfolio or Index to Monitor"
                          hint="Use 'WhaleTrader Portfolio Equity' to guard your own portfolio"
                          persistent-hint
                        />
                        <v-text-field
                          v-model="editedMt.portfolio_equity.days"
                          style="max-width: 200px"
                          class="mt-3"
                          type="number"
                          variant="outlined"
                          density="compact"
                          label="Lookback Days"
                          hint="Performance is measured over this many trading days"
                          persistent-hint
                        />
                        <v-text-field
                          v-model="editedMt.portfolio_equity.percent_increase"
                          style="max-width: 250px"
                          class="mt-3"
                          type="number"
                          variant="outlined"
                          density="compact"
                          label="Performance % (negative = decline)"
                          hint="e.g. -10 to trigger when down 10% over the lookback period"
                          persistent-hint
                        />
                      </v-col>

                      <v-col v-if="editedMt.sell_trigger == 12" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when the largest whale-held sector within a WhaleIndex becomes
                          overly concentrated — a classic bubble warning. When institutional money
                          is crowded into a single sector (e.g. Technology above 40%), market risk
                          is elevated and a rotation out of that sector can cause outsized losses.
                          A threshold of 35–40% is a reasonable starting point for most markets.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.sector_concentration.whale_index_id"
                              style="max-width: 350px"
                              :items="whale_indexes"
                              item-title="name"
                              item-value="whale_index_id"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Choose WhaleIndex"
                            />
                          </v-col>
                          <v-col cols="12">
                            <v-text-field
                              v-model="editedMt.sector_concentration.threshold"
                              style="max-width: 250px"
                              class="mt-2"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Sector Concentration Threshold (%)"
                              hint="Trigger when any single sector exceeds this % of the index"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Rule 13: Euphoria breadth -->
                      <v-col v-if="editedMt.sell_trigger == 13" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when an unusually high percentage of stocks across the entire
                          universe have gained strongly over a recent period — a sign of broad
                          market euphoria or a blow-off top. Near market peaks, even low-quality
                          stocks tend to rally sharply as retail speculation intensifies. A
                          typical setting is: 50% of stocks gained ≥20% over 60 trading days.
                          Tighten the thresholds for an earlier, more conservative signal.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="editedMt.stock_perf.days"
                              :items="perf_days"
                              item-title="text"
                              item-value="value"
                              variant="outlined"
                              density="compact"
                              label="Lookback Period"
                              hide-details
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.stock_perf.threshold"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Minimum Gain (%)"
                              hint="Stocks must have gained at least this % in the lookback period"
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.stock_perf.percent_of_stocks"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="% of Universe Stocks"
                              hint="Trigger when at least this % of all stocks meet the gain threshold"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Rule 14: NATR complacency -->
                      <v-col v-if="editedMt.sell_trigger == 14" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when the average Normalized Average True Range (NATR) across
                          the stock universe drops to an unusually low level — a classic
                          complacency signal. NATR measures daily price range as a percentage of
                          price; very low readings indicate that the market has stopped pricing in
                          risk. Historically, extended periods of very low volatility (VIX below
                          12, NATR below 2–3) often precede sharp corrections. Select "below" to
                          trigger when the market is unusually calm.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" sm="4">
                            <v-select
                              v-model="editedMt.natr.above_below"
                              :items="['above', 'below']"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Above / Below"
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-text-field
                              v-model="editedMt.natr.threshold"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Average NATR Threshold"
                              hint="Typical range: 2–8. Below 3 = complacency."
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Rule 15: WhaleIndex holdings avg perf -->
                      <v-col v-if="editedMt.sell_trigger == 15" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when the average unrealized gain of stocks currently held in a
                          WhaleIndex exceeds a threshold over the selected lookback period. This
                          measures whether the index's holdings have run up significantly —
                          suggesting the signal source (institutional consensus) is over-extended
                          and ripe for mean reversion. For example, if the average stock in the
                          WhaleIndex is up 25% over 60 days, that broad momentum may be
                          unsustainable. Typical settings: <strong>60 days / 25% threshold</strong>.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.whaleindex_perf.whale_index_id"
                              :items="whale_indexes"
                              item-title="name"
                              item-value="whale_index_id"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="WhaleIndex to Monitor"
                              style="max-width: 350px"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="editedMt.whaleindex_perf.days"
                              :items="perf_days"
                              item-title="text"
                              item-value="value"
                              variant="outlined"
                              density="compact"
                              label="Lookback Period"
                              hint="Measure average performance of holdings over this many days"
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.whaleindex_perf.threshold"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Average Gain Threshold (%)"
                              hint="Trigger when the average holding in the index is up more than this %"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Rule 16: WhaleIndex quarterly turnover -->
                      <v-col v-if="editedMt.sell_trigger == 16" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when the percentage of new positions added to a WhaleIndex
                          relative to the prior quarter exceeds a threshold. High portfolio
                          turnover among institutional managers signals broad repositioning —
                          a potential shift in consensus that can lead to elevated market
                          volatility as large blocks of stock change hands. A reading of 40%+
                          means nearly half the index's holdings are brand new this quarter,
                          which is unusually high and may warrant caution.
                          Typical setting: <strong>40% threshold</strong>.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12">
                            <v-select
                              v-model="editedMt.whaleindex_turnover.whale_index_id"
                              :items="whale_indexes"
                              item-title="name"
                              item-value="whale_index_id"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="WhaleIndex to Monitor"
                              style="max-width: 350px"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              v-model="editedMt.whaleindex_turnover.threshold"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="New Position Turnover Threshold (%)"
                              hint="Trigger when new positions represent more than this % of index holdings vs prior quarter"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Rule 17: Macro indicator spread (yield curve) -->
                      <v-col v-if="editedMt.sell_trigger == 17" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when the mathematical spread between two macro indicators
                          (Indicator 1 <strong>minus</strong> Indicator 2) crosses a threshold.
                          The classic use is detecting <strong>yield curve inversion</strong>:
                          set Indicator 1 to the 10-Year Treasury yield and Indicator 2 to the
                          Fed Funds Rate, then trigger when the spread falls <strong>below 0</strong>.
                          An inverted yield curve has historically preceded recessions with high
                          reliability. You can also use this to monitor other macro divergences,
                          such as VIX minus a long-term VIX average, or two regional interest
                          rates. Threshold of <strong>0</strong> is the most common setting for
                          inversion signals.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="editedMt.macro_spread.macro_data_type_id_1"
                              :items="spread_macro_types"
                              item-title="description"
                              item-value="macro_data_type_id"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Indicator 1 (the value subtracted from)"
                            />
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              v-model="editedMt.macro_spread.macro_data_type_id_2"
                              :items="spread_macro_types"
                              item-title="description"
                              item-value="macro_data_type_id"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Indicator 2 (subtracted from Indicator 1)"
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-select
                              v-model="editedMt.macro_spread.above_below"
                              :items="['above', 'below']"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Spread is Above / Below Threshold"
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-text-field
                              v-model="editedMt.macro_spread.threshold"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Threshold Value"
                              hint="Use 0 for yield curve inversion (Indicator 1 − Indicator 2 &lt; 0)"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <!-- Rule 18: Insider sell/buy ratio MA -->
                      <v-col v-if="editedMt.sell_trigger == 18" class="mt-2 pl-4" cols="12">
                        <p class="text-body-2 mb-3">
                          Triggers when the moving average of the WhaleWisdom insider sell/buy
                          ratio — calculated over the last N weeks — exceeds or falls below a
                          threshold. The ratio compares the dollar value of insider sells to
                          insider buys across all tracked companies. A ratio <strong>above 1.5</strong>
                          means insiders are selling at 1.5× the rate they are buying, which
                          signals broad executive-level caution. Smoothing with a multi-week
                          moving average filters out single-week spikes from scheduled 10b5-1
                          plan sales. A sustained elevated ratio (e.g. 4-week average above 2.0)
                          is historically associated with market tops. Select <strong>"above"</strong>
                          and a threshold of <strong>1.5</strong> as a starting point.
                        </p>
                        <v-row density="compact">
                          <v-col cols="12" sm="4">
                            <v-text-field
                              v-model="editedMt.insider_ma.weeks"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Moving Average (Weeks)"
                              hint="Number of weeks to smooth the sell/buy ratio — 4 weeks is a common starting point"
                              persistent-hint
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-select
                              v-model="editedMt.insider_ma.above_below"
                              :items="['above', 'below']"
                              variant="outlined"
                              density="compact"
                              hide-details
                              label="Ratio is Above / Below Threshold"
                            />
                          </v-col>
                          <v-col cols="12" sm="4">
                            <v-text-field
                              v-model="editedMt.insider_ma.threshold"
                              type="number"
                              variant="outlined"
                              density="compact"
                              label="Sell/Buy Ratio Threshold"
                              hint="e.g. 1.5 means insiders are selling 1.5× more than buying (in dollar terms)"
                              persistent-hint
                            />
                          </v-col>
                        </v-row>
                      </v-col>

                      <v-col cols="12">
                        <h3 class="text-h5 mt-4">When to resume normal trading:</h3>
                        <v-row density="compact">
                          <v-col cols="12">
                            <v-select
                              v-model="editedResume.resume_indicator_id"
                              variant="outlined"
                              density="compact"
                              :items="enabled_resume_indicators(editedMt.sell_trigger)"
                              item-value="id"
                              item-title="description"
                              label="Choose Resume Normal Trading Trigger"
                            />
                          </v-col>
                          <v-col cols="auto">
                            <v-btn
                              :disabled="!editedResume.resume_indicator_id"
                              @click="openOrAddResume"
                            >
                              <span v-if="editedResume.resume_indicator_id == 999">Add</span
                              ><span v-else>Configure</span>
                            </v-btn>
                            <v-dialog v-model="resumeDialog" max-width="700px">
                              <v-card>
                                <v-card-title>
                                  <span class="text-h5">{{ ResumeTitle }}</span>
                                </v-card-title>

                                <v-card-text>
                                  <v-container>
                                    <v-row density="compact">
                                      <v-col cols="12">
                                        <v-checkbox
                                          v-model="editedResume.enabled"
                                          label="Enable Resume Rule"
                                          color="primary"
                                          hide-details
                                          density="compact"
                                          class="mt-0"
                                        />
                                      </v-col>
                                      <v-col
                                        v-if="editedMt.and_or_indicators == 'sequenced'"
                                        cols="12"
                                      >
                                        <v-text-field
                                          v-model="editedResume.ordering"
                                          style="max-width: 200px"
                                          type="number"
                                          variant="outlined"
                                          density="compact"
                                          label="Sequence Order"
                                        />
                                      </v-col>
                                      <v-col
                                        v-show="editedResume.resume_indicator_id == 1"
                                        cols="12"
                                        md="6"
                                      >
                                        <v-text-field
                                          v-model="editedResume.percent"
                                          variant="outlined"
                                          density="compact"
                                          hide-details
                                          label="optional (price is % above MA)"
                                        />
                                      </v-col>
                                      <v-col
                                        v-if="editedResume.resume_indicator_id == 2"
                                        class="mt-2 pl-4"
                                        cols="12"
                                      >
                                        <v-select
                                          v-model="editedResume.macro_data_type_id"
                                          variant="outlined"
                                          density="compact"
                                          :items="macro_indicators"
                                          label="Choose Macro Indicator"
                                          item-value="macro_data_type_id"
                                          item-title="description"
                                          class="shrink"
                                        />
                                        <div
                                          v-show="
                                            editedResume.macro_data_type_id != 'outperformed' &&
                                            editedResume.macro_data_type_id != 'underperformed'
                                          "
                                        >
                                          <v-select
                                            v-model="editedResume.above_below"
                                            variant="outlined"
                                            density="compact"
                                            :items="above_below"
                                            label="Above/Below"
                                            class="shrink"
                                          />

                                          <v-text-field
                                            v-if="
                                              editedResume.above_below == 'above' ||
                                              editedResume.above_below == 'below'
                                            "
                                            v-model="editedResume.threshold"
                                            variant="outlined"
                                            style="max-width: 300px"
                                            density="compact"
                                            label="Threshold"
                                          />
                                          <v-select
                                            v-else
                                            v-model="editedResume.previous_reading"
                                            style="max-width: 300px"
                                            :items="[
                                              '1 day ago',
                                              '1 week ago',
                                              '1 month ago',
                                              '3 months ago',
                                              '6 months ago',
                                              '1 year ago',
                                              'last changed reading'
                                            ]"
                                            hide-details
                                            variant="outlined"
                                            density="compact"
                                            label="Previous Reading to Use"
                                          />
                                        </div>
                                      </v-col>
                                      <v-col
                                        v-if="
                                          editedResume.macro_data_type_id == 'outperformed' ||
                                          editedResume.macro_data_type_id == 'underperformed'
                                        "
                                        cols="12"
                                        class="mt-2 pl-4"
                                      >
                                        <v-row density="compact">
                                          <v-col cols="12">
                                            <v-select
                                              v-model="editedResume.index1"
                                              style="max-width: 300px"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index 1"
                                            />
                                          </v-col>
                                          <v-col cols="12">
                                            <v-select
                                              v-model="editedResume.index2"
                                              style="max-width: 300px"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index 2"
                                            />
                                          </v-col>
                                          <v-col cols="12">
                                            <v-text-field
                                              v-model="editedResume.threshold"
                                              variant="outlined"
                                              density="compact"
                                              style="max-width: 300px"
                                              label="Percentage Points Difference"
                                            />
                                          </v-col>
                                          <v-col cols="12">
                                            <v-select
                                              v-model="editedResume.days"
                                              density="compact"
                                              hide-details
                                              style="max-width: 300px"
                                              variant="outlined"
                                              :items="stocks_vs_sp_months"
                                              item-title="text"
                                              item-value="value"
                                              label="# Days Out/Under Performed"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                      <v-col
                                        v-if="
                                          editedResume.resume_indicator_id == 2 &&
                                          editedResume.macro_data_type_id == 8
                                        "
                                        class="mt-2 pl-4"
                                        cols="12"
                                      >
                                        <v-checkbox
                                          v-model="editedResume.ma_enabled"
                                          label="Use Moving Average"
                                          color="primary"
                                          hide-details
                                          density="compact"
                                          class="mt-0"
                                        />

                                        <v-text-field
                                          v-model="editedResume.madays"
                                          variant="outlined"
                                          density="compact"
                                          label="Moving Average Days"
                                        />
                                      </v-col>
                                      <v-col
                                        v-show="
                                          editedResume.resume_indicator_id == 3 ||
                                          editedResume.resume_indicator_id == 9
                                        "
                                        cols="12"
                                        md="8"
                                      >
                                        <v-select
                                          v-model="editedResume.index"
                                          :items="indices"
                                          item-title="text"
                                          hide-details
                                          variant="outlined"
                                          density="compact"
                                          label="Choose Index"
                                        />
                                      </v-col>

                                      <v-col
                                        v-show="editedResume.resume_indicator_id == 3"
                                        cols="12"
                                        md="4"
                                      >
                                        <v-text-field
                                          v-model="editedResume.threshold"
                                          variant="outlined"
                                          density="compact"
                                          hide-details
                                          label="% decline in index"
                                        />
                                      </v-col>
                                      <v-col
                                        v-show="editedResume.resume_indicator_id == 9"
                                        cols="12"
                                        md="4"
                                      >
                                        <v-text-field
                                          v-model="editedResume.threshold"
                                          variant="outlined"
                                          density="compact"
                                          hide-details
                                          label="% increase in index"
                                        />
                                      </v-col>
                                      <v-col
                                        v-if="
                                          editedResume.resume_indicator_id == 4 ||
                                          editedResume.resume_indicator_id == 5
                                        "
                                      >
                                        <v-row density="compact">
                                          <v-col cols="12" md="8">
                                            <v-select
                                              v-model="editedResume.index"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index"
                                            />
                                          </v-col>
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.ma1days"
                                              variant="outlined"
                                              density="compact"
                                              hide-details
                                              label="1st MA # of days"
                                            />
                                          </v-col>
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.ma2days"
                                              variant="outlined"
                                              density="compact"
                                              hide-details
                                              label="2nd MA # of days"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                      <v-col v-if="editedResume.resume_indicator_id == 6">
                                        <v-row density="compact">
                                          <v-col cols="12" md="8">
                                            <v-select
                                              v-model="editedResume.index"
                                              style="max-width: 300px"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index"
                                            />
                                          </v-col>
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.day_low"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="# of days for new low"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                      <v-col v-if="editedResume.resume_indicator_id == 7">
                                        <v-row density="compact">
                                          <v-col cols="12" md="8">
                                            <v-select
                                              v-model="editedResume.index"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index"
                                            />
                                          </v-col>
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.day_high"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="# of days for new high"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                      <v-col v-if="editedResume.resume_indicator_id == 8">
                                        <v-row>
                                          <v-col cols="12" md="9">
                                            <v-select
                                              v-model="editedResume.index"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index"
                                            />
                                          </v-col>
                                          <v-col cols="12" md="4">
                                            <v-text-field
                                              v-model="editedResume.day_high"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="# of days for new high"
                                            />
                                          </v-col>
                                          <v-col cols="12" md="4">
                                            <v-text-field
                                              v-model="editedResume.threshold"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="% decline from high"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                      <v-col v-if="editedResume.resume_indicator_id == 10">
                                        <v-row>
                                          <v-col cols="12" md="9">
                                            <v-select
                                              v-model="editedResume.index"
                                              :items="indices"
                                              item-title="text"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="Choose Index"
                                            />
                                          </v-col>
                                          <v-col cols="12" md="4">
                                            <v-text-field
                                              v-model="editedResume.day_high"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="# of days for 1st new high"
                                            />
                                          </v-col>
                                          <v-col cols="12" md="4">
                                            <v-text-field
                                              v-model="editedResume.threshold"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="# ATR Below 2nd high"
                                            />
                                          </v-col>
                                          <v-col cols="12" md="4">
                                            <v-text-field
                                              v-model="editedResume.day_high2"
                                              style="max-width: 200px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="# of days for 2nd new high"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                      <v-col
                                        v-if="
                                          editedResume.resume_indicator_id == 11 ||
                                          editedResume.resume_indicator_id == 12
                                        "
                                      >
                                        <v-row density="compact">
                                          <v-col>
                                            <v-select
                                              v-model="editedResume.ma1days"
                                              :items="ma_days"
                                              label="Moving Average Days"
                                              hint="Moving Average"
                                              density="compact"
                                              style="max-width: 200px"
                                              hide-details
                                              variant="outlined"
                                            />
                                          </v-col>
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.threshold"
                                              variant="outlined"
                                              type="number"
                                              density="compact"
                                              persistent-hint
                                              label="% of Stocks"
                                              :hint="
                                                'Percent ' +
                                                (editedMt.sell_trigger == 7 ? 'above' : 'below') +
                                                ' moving average'
                                              "
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>

                                      <v-col v-if="editedResume.resume_indicator_id == 13">
                                        <v-row density="compact">
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.ma1days"
                                              variant="outlined"
                                              density="compact"
                                              hide-details
                                              label="1st MA # of days"
                                            />
                                          </v-col>
                                          <v-col cols="12" md="8">
                                            <v-select
                                              v-model="editedResume.compare_to"
                                              :items="['is above', 'is below']"
                                              hide-details
                                              variant="outlined"
                                              density="compact"
                                              label="How to compare"
                                            />
                                          </v-col>
                                          <v-col>
                                            <v-text-field
                                              v-model="editedResume.ma2days"
                                              variant="outlined"
                                              density="compact"
                                              hide-details
                                              label="2nd MA # of days"
                                            />
                                          </v-col>
                                          <v-col cols="12" sm="6">
                                            <v-text-field
                                              v-model="editedResume.percent_change"
                                              style="max-width: 300px"
                                              type="number"
                                              variant="outlined"
                                              density="compact"
                                              label="% difference vs 1st MA (optional)"
                                            />
                                          </v-col>
                                        </v-row>
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-card-text>

                                <v-card-actions>
                                  <div class="flex-grow-1" />
                                  <v-btn @click="closeResumeDialog"> Cancel Add </v-btn>
                                  <v-btn @click="saveResume"> Add </v-btn>
                                </v-card-actions>
                              </v-card>
                            </v-dialog>
                          </v-col>
                          <v-col cols="12">
                            <v-radio-group v-model="editedMt.and_or_indicators" row hide-details>
                              <v-radio
                                label="All enabled rules below must match before resuming normal trading"
                                value="and"
                                color="primary"
                                hint=""
                                persistent-hint
                              />
                              <v-radio
                                label="Any enabled rule below that matches will trigger normal trading"
                                value="or"
                                color="primary"
                                hint=""
                                persistent-hint
                              />
                              <v-radio
                                label="The ordered sequence of events below will trigger normal trading"
                                value="sequenced"
                                color="primary"
                                hint=""
                                persistent-hint
                              />
                            </v-radio-group>
                          </v-col>
                        </v-row>
                        <v-data-table
                          :headers="resume_headers"
                          :items="editedMt.resume_triggers"
                          hide-default-footer
                          items-per-page="-1"
                        >
                          <template #item.ordering="{ item }">
                            <span v-if="editedMt.and_or_indicators == 'sequenced'">
                              <v-text-field
                                v-model="item.ordering"
                                style="max-width: 200px"
                                type="number"
                                variant="outlined"
                                density="compact"
                                label=""
                              />
                            </span>
                            <span v-else> Not used </span>
                          </template>
                          <template #item.enabled="{ item }">
                            <v-checkbox
                              v-model="item.enabled"
                              label=""
                              color="primary"
                              hide-details
                              density="compact"
                              class="mt-0"
                            />
                          </template>
                          <template #item.resume_indicator_id="{ item }">
                            <span v-if="item.resume_indicator_id == 1">
                              When {{ LookupIndiceName(item.index) }} price goes back above the its
                              defined moving average.
                              <span v-if="item.percent">
                                price is {{ item.percent }}% above MA</span
                              >
                            </span>
                            <span v-if="item.resume_indicator_id == 2">
                              Macro Indicator
                              {{ GetMacroIndicatorDescription(item.macro_data_type_id) }}
                              <div
                                v-if="
                                  item.macro_data_type_id == 'outperformed' ||
                                  item.macro_data_type_id == 'underperformed'
                                "
                              >
                                {{ LookupIndiceName(item.index1) }}
                                <span v-if="item.macro_data_type_id == 'outperformed'"
                                  >Outperformed</span
                                ><span v-else>Underperformed</span>
                                {{ LookupIndiceName(item.index2) }}
                                by {{ item.threshold }} percentage points over
                                {{ GetDaysSelect(item.days) }}
                              </div>
                              <div v-else>
                                {{ item.above_below }}
                                {{ item.threshold }}.
                                <span v-if="item.macro_data_type_id == 8"
                                  >CBOE VIX is a registered trademark of Cboe Exchange, Inc.</span
                                >
                                <span v-if="item.ma_enabled"
                                  >{{ item.madays }} day moving average used.</span
                                >
                              </div>
                            </span>
                            <span v-if="item.resume_indicator_id == 3">
                              {{ LookupIndiceName(item.index) }} Price Decline of
                              {{ item.threshold }}%
                            </span>
                            <span v-if="item.resume_indicator_id == 4">
                              Death Cross {{ item.ma2days }} day MA drops below
                              {{ item.ma1days }} day MA
                            </span>
                            <span v-if="item.resume_indicator_id == 5">
                              Golden Cross {{ item.ma2days }} day MA is above {{ item.ma1days }} day
                              MA
                            </span>
                            <span v-if="item.resume_indicator_id == 6">
                              {{ LookupIndiceName(item.index) }} reaches {{ item.day_low }} day low
                            </span>
                            <span v-if="item.resume_indicator_id == 7">
                              {{ LookupIndiceName(item.index) }} reaches {{ item.day_high }} day
                              high
                            </span>
                            <span v-if="item.resume_indicator_id == 8">
                              {{ LookupIndiceName(item.index) }} {{ item.threshold }}% Decline from
                              the Highest High of {{ item.day_high }} Past Days
                            </span>
                            <span v-if="item.resume_indicator_id == 9">
                              {{ LookupIndiceName(item.index) }} Price Increase of
                              {{ item.threshold }}%
                            </span>
                            <span v-if="item.resume_indicator_id == 10">
                              {{ LookupIndiceName(item.index) }} makes a {{ item.day_high }} high
                              that is at least {{ item.threshold }} ATR below the
                              {{ item.day_high2 }} day high
                            </span>
                            <span v-if="item.resume_indicator_id == 11">
                              {{ item.threshold }}% of stocks are trading above their
                              {{ item.ma1days }} day MA
                            </span>
                            <span v-if="item.resume_indicator_id == 12">
                              {{ item.threshold }}% of stocks are trading below their
                              {{ item.ma1days }} day MA
                            </span>
                            <span v-if="item.resume_indicator_id == 13">
                              Portfolio Equity {{ item.ma1days }} day MA {{ item.compare_to }}
                              {{ item.ma2days }} day MA.
                              <span v-if="item.percent_change && item.percent_change != 0"
                                >{{ item.percent_change }} difference in moving averages</span
                              >
                            </span>
                          </template>
                          <template #item.action="{ item }">
                            <v-icon small class="mr-2" @click="editResume(item)">
                              {{ mdiPencil }}
                            </v-icon>

                            <v-icon small @click="delete_resume(item)">
                              {{ mdiDelete }}
                            </v-icon>
                          </template>
                        </v-data-table>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <div class="flex-grow-1" />
                  <v-btn @click="close"> Cancel </v-btn>
                  <v-btn
                    color="primary"
                    :disabled="editedMt.resume_triggers.length == 0 || !editedMt.sell_trigger"
                    @click="save"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          <template #item.action="{ item }">
            <v-icon small class="mr-2" @click="edit(item)">
              {{ mdiPencil }}
            </v-icon>

            <v-icon small @click="delete_item(item)">
              {{ mdiDelete }}
            </v-icon>
          </template>

          <template #item.sell_trigger="{ item }">
            <span v-if="item.sell_trigger == 1">
              {{ LookupIndiceName(item.moving_average.index) }} Price Drops Below Moving Average:
              {{ item.moving_average.days }} day MA.
              <span v-if="item.moving_average.percent"
                >% price is {{ item.moving_average.percent }}% below MA</span
              >
            </span>
            <span
              v-else-if="
                item.sell_trigger == 2 && item.index_performance.compare_to == 'moving average'
              "
            >
              Price Appreciation Above Moving Average:
              {{ LookupIndiceName(item.index_performance.index) }}
              {{ item.index_performance.compare_to_ma }} day MA is
              {{ item.index_performance.percent_increase }}% above
              {{ item.index_performance.days }} day MA
            </span>
            <span
              v-else-if="item.sell_trigger == 2 && item.index_performance.compare_to == 'price'"
            >
              Price Appreciation Above Moving Average:
              {{ LookupIndiceName(item.index_performance.index) }} price is
              {{ item.index_performance.percent_increase }}% above
              {{ item.index_performance.days }} day MA
            </span>
            <span v-else-if="item.sell_trigger == 3">
              {{ GetMacroIndicatorDescription(item.macro_indicator.macro_data_type_id) }}

              <div
                v-if="
                  item.macro_indicator.macro_data_type_id == 'outperformed' ||
                  item.macro_indicator.macro_data_type_id == 'underperformed'
                "
              >
                {{ LookupIndiceName(item.macro_indicator.index1) }}
                <span v-if="item.macro_indicator.macro_data_type_id == 'outperformed'"
                  >Outperformed</span
                ><span v-else>Underperformed</span>
                {{ LookupIndiceName(item.macro_indicator.index2) }}
                by {{ item.macro_indicator.threshold }} percentage points over
                {{ GetDaysSelect(item.macro_indicator.days) }}
              </div>
              <div v-else>
                {{ item.macro_indicator.above_below }}
                {{ item.macro_indicator.threshold }}.
                <span v-if="item.macro_indicator.macro_data_type_id == 8"
                  >CBOE VIX is a registered trademark of Cboe Exchange, Inc.</span
                >
                <span v-if="item.macro_indicator.ma_enabled"
                  >{{ item.macro_indicator.madays }} day moving average used.</span
                >
              </div>
            </span>
            <span v-else-if="item.sell_trigger == 4">
              {{ LookupIndiceName(item.day_low_high_index) }} reaches {{ item.day_low_high }} day
              low
            </span>
            <span v-else-if="item.sell_trigger == 5">
              {{ LookupIndiceName(item.day_low_high_index) }} reaches {{ item.day_low_high }} day
              high
            </span>

            <span v-else-if="item.sell_trigger == 6">
              {{ LookupIndiceName(item.moving_average.index) }} {{ item.moving_average.days }} day
              MA is {{ item.moving_average.over_under }} {{ item.moving_average.days2 }} day MA
            </span>
            <span v-else-if="item.sell_trigger == 7">
              {{ item.moving_average.percent }}% of Stocks are Trading Above Their
              {{ item.moving_average.days }} day MA
            </span>
            <span v-else-if="item.sell_trigger == 8">
              {{ item.moving_average.percent }}% of Stocks are Trading Below Their
              {{ item.moving_average.days }} day MA
            </span>
            <span v-else-if="item.sell_trigger == 10">
              Portfolio Equity {{ item.portfolio_equity.days }} day MA
              {{ item.portfolio_equity.compare_to }} {{ item.portfolio_equity.compare_to_ma }} day
              MA.
              <span
                v-if="
                  item.portfolio_equity.percent_increase &&
                  item.portfolio_equity.percent_increase != 0
                "
              >
                {{ item.portfolio_equity.percent_increase }} difference in moving averages</span
              >
            </span>
            <span v-else-if="item.sell_trigger == 11">
              {{ LookupIndiceName(item.portfolio_equity.index) }}
              {{ item.portfolio_equity.days }} day performance is >=
              {{ item.portfolio_equity.percent_increase }}%
            </span>
            <span v-else-if="item.sell_trigger == 12">
              WhaleIndex Sector Concentration:
              {{ GetWhaleIndexName(item.sector_concentration.whale_index_id) }} has a sector
              exceeding {{ item.sector_concentration.threshold }}%
            </span>
            <span v-else-if="item.sell_trigger == 13">
              Euphoria Breadth: &ge;{{ item.stock_perf.percent_of_stocks }}% of stocks gained
              &ge;{{ item.stock_perf.threshold }}% over {{ item.stock_perf.days }} days
            </span>
            <span v-else-if="item.sell_trigger == 14">
              NATR Complacency: avg NATR is {{ item.natr.above_below }}
              {{ item.natr.threshold }}
            </span>
            <span v-else-if="item.sell_trigger == 15">
              WhaleIndex Holdings Perf:
              {{ GetWhaleIndexName(item.whaleindex_perf.whale_index_id) }} avg gain &ge;
              {{ item.whaleindex_perf.threshold }}% over {{ item.whaleindex_perf.days }} days
            </span>
            <span v-else-if="item.sell_trigger == 16">
              WhaleIndex Turnover:
              {{ GetWhaleIndexName(item.whaleindex_turnover.whale_index_id) }} new positions
              &ge;{{ item.whaleindex_turnover.threshold }}% vs prior quarter
            </span>
            <span v-else-if="item.sell_trigger == 17">
              Macro Spread:
              {{ GetMacroIndicatorDescription(item.macro_spread.macro_data_type_id_1) }} &minus;
              {{ GetMacroIndicatorDescription(item.macro_spread.macro_data_type_id_2) }} is
              {{ item.macro_spread.above_below }} {{ item.macro_spread.threshold }}
            </span>
            <span v-else-if="item.sell_trigger == 18">
              Insider Ratio MA: {{ item.insider_ma.weeks }}-week avg sell/buy ratio is
              {{ item.insider_ma.above_below }} {{ item.insider_ma.threshold }}
            </span>
            <span v-else> Unknown {{ item.sell_trigger }} </span>
          </template>
          <template #item.enabled="{ item }">
            <v-switch v-model="item.enabled" color="primary" />
          </template>
          <template #item.resume_triggers="{ item }">
            <span v-if="item.and_or_indicators == 'sequenced'">
              ordered sequence of events below:
            </span>
            <ul v-for="rt in item.resume_triggers" :key="rt.resume_indicator_id">
              <li style="clear: both; list-style-type: none">
                <v-switch
                  v-model="rt.enabled"
                  class="float-left mr-2"
                  color="primary"
                  :disabled="!item.enabled"
                />
                <span v-if="item.and_or_indicators == 'sequenced'"> {{ rt.ordering }}: </span>
                <span v-if="rt.resume_indicator_id == 1">
                  When {{ LookupIndiceName(rt.index) }} price goes back above the defined moving
                  average.
                  <span v-if="rt.percent">% price is {{ rt.percent }}% above MA</span>
                </span>
                <span v-if="rt.resume_indicator_id == 2">
                  Macro Indicator: {{ GetMacroIndicatorDescription(rt.macro_data_type_id) }}
                  <div
                    v-if="
                      rt.macro_data_type_id == 'outperformed' ||
                      rt.macro_data_type_id == 'underperformed'
                    "
                  >
                    {{ LookupIndiceName(rt.index1) }}
                    <span v-if="rt.macro_data_type_id == 'outperformed'">Outperformed</span
                    ><span v-else>Underperformed</span>
                    {{ LookupIndiceName(rt.index2) }}
                    by {{ rt.threshold }} percentage points over {{ GetDaysSelect(rt.days) }}
                  </div>
                  <div
                    v-else-if="
                      rt.macro_data_type_id == 9 &&
                      (rt.above_below == 'above a prior reading' ||
                        rt.above_below == 'below a prior reading')
                    "
                  >
                    <span v-if="rt.above_below == 'above a prior reading'"
                      >above the reading {{ rt.previous_reading }}</span
                    >
                    <span v-else>below the reading {{ rt.previous_reading }}</span>
                  </div>
                  <div v-else>
                    {{ rt.above_below }}
                    {{ rt.threshold }}.
                    <span v-if="rt.macro_data_type_id == 8"
                      >CBOE VIX is a registered trademark of Cboe Exchange, Inc.</span
                    >
                    <span v-if="rt.ma_enabled">{{ rt.madays }} day moving average used.</span>
                  </div>
                </span>
                <span v-if="rt.resume_indicator_id == 3">
                  {{ LookupIndiceName(rt.index) }} Price Decline of {{ rt.threshold }}%
                </span>
                <span v-if="rt.resume_indicator_id == 4">
                  Death Cross {{ rt.ma2days }} day MA is below {{ rt.ma1days }} day MA
                </span>
                <span v-if="rt.resume_indicator_id == 5">
                  Golden Cross {{ rt.ma2days }} day MA is above {{ rt.ma1days }} day MA
                </span>
                <span v-if="rt.resume_indicator_id == 6">
                  {{ LookupIndiceName(rt.index) }} reaches {{ rt.day_low }} day low
                </span>
                <span v-if="rt.resume_indicator_id == 7">
                  {{ LookupIndiceName(rt.index) }} reaches {{ rt.day_high }} day high
                </span>
                <span v-if="rt.resume_indicator_id == 8">
                  {{ LookupIndiceName(rt.index) }} {{ rt.threshold }}% Decline from the Highest High
                  of {{ rt.day_high }} Past Days
                </span>
                <span v-if="rt.resume_indicator_id == 9">
                  {{ LookupIndiceName(rt.index) }} Price Increase of {{ rt.threshold }}%
                </span>
                <span v-if="rt.resume_indicator_id == 10">
                  {{ LookupIndiceName(rt.index) }} makes a {{ rt.day_high }} high that is at least
                  {{ rt.threshold }} ATR below the {{ rt.day_high2 }} day high
                </span>
                <span v-if="rt.resume_indicator_id == 11">
                  {{ rt.threshold }}% of stocks are trading above their {{ rt.ma1days }} day MA
                </span>
                <span v-if="rt.resume_indicator_id == 12">
                  {{ rt.threshold }}% of stocks are trading below their {{ rt.ma1days }} day MA
                </span>
                <span v-if="rt.resume_indicator_id == 13">
                  Portfolio Equity {{ rt.ma1days }} day MA {{ rt.compare_to }} {{ rt.ma2days }} day
                  MA.
                  <span v-if="rt.percent_change && rt.percent_change != 0"
                    >{{ rt.percent_change }} difference in moving averages</span
                  >
                </span>
              </li>
            </ul>
          </template>
          <template #no-data> No market timing rules configured </template>
        </v-data-table>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapStoreState, mapFields } from '@/store/helpers'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import AccessMixin from '@/mixins/access'
import cloneDeep from 'lodash/cloneDeep'
import { mdiDelete, mdiPencil } from '@mdi/js'
export default {
  mixins: [AccessMixin],
  data() {
    return {
      mdiDelete,
      mdiPencil,
      ma_days: [],
      resumeDialog: false,
      stocks_vs_sp_months: [
        { text: '20 trading days', value: 1 },
        { text: '60 trading days', value: 3 },
        { text: '120 trading days', value: 6 },
        { text: '180 trading days', value: 9 },
        { text: '240 trading days', value: 12 },
        { text: '480 trading days', value: 24 }
      ],
      indices: [
        { value: 169819, text: 'S&P 500  Total Return Index' },
        { value: 169823, text: 'Russell 2000 Total Return Index' },
        { value: 'portfolio', text: 'WhaleTrader Portfolio Equity' },
        { value: 4193, text: 'Invesco QQQ Trust Series' },
        { value: 7595, text: 'SPDR S&P Biotech ETF' },
        { value: 5910, text: 'SPDR Materials Select Sector ETF' },
        { value: 4145, text: 'SPDR Energy Select Sector Fund' },
        { value: 5963, text: 'SPDR S&P Financial ETF' },
        { value: 4180, text: 'SPDR S&P Industrial ETF' },
        { value: 5593, text: 'SPDR S&P Technology ETF' },
        { value: 6764, text: 'SPDR S&P Consumer Staples ETF' },
        { value: 5018, text: 'SPDR S&P Utilities ETF' },
        { value: 5961, text: 'SPDR S&P Health Care ETF' },
        { value: 5962, text: 'SPDR Consumer Discretionary Select Sector ETF' },
        { value: 120077, text: 'VanEck Vectors Semiconductor ETF (SMH)' },
        { value: 3940, text: 'iShares U.S. Real Estate ETF (IYR)' },
        { value: 7266, text: 'iShares 20+ Year Treasury Bond ETF (TLT)' },
        { value: 4114, text: 'iShares Russell 2000 Value ETF (IWN)' },
        { value: 85889, text: 'iShares Russell Top 200 Growth ETF (IWY)' }
      ],
      sell_triggers: [
        { id: 1, description: 'Index Price Drops Below Moving Average' },
        { id: 2, description: 'Price Appreciation Above Moving Average' },
        { id: 3, description: 'Macro Indicator' },
        { id: 4, description: 'Index makes an __ day low' },
        { id: 5, description: 'Index makes an __ day high' },
        { id: 6, description: 'Index moving average over/under 2nd moving average' },
        { id: 7, description: '% of stocks (entire universe) trading above moving average' },
        { id: 8, description: '% of stocks (entire universe) trading below moving average' },
        { id: 10, description: 'Portfolio Equity Moving Average Changes' },
        { id: 11, description: 'Portfolio or Index Performance' },
        { id: 12, description: 'WhaleIndex Sector Concentration' },
        { id: 13, description: '% of Stocks with Strong Recent Gains (Euphoria Breadth)' },
        { id: 14, description: 'Low Average Market Volatility (NATR Complacency)' },
        { id: 15, description: 'WhaleIndex Holdings Average Performance (Extended Positions)' },
        { id: 16, description: 'WhaleIndex Quarterly Turnover (Manager Repositioning)' },
        { id: 17, description: 'Macro Indicator Spread (e.g. Yield Curve)' },
        { id: 18, description: 'Insider Sell/Buy Ratio Moving Average' }
        //  { id: 6, description: 'CBOE Volatility Index: VIX Above' }
      ],
      macro_indicators: [
        /* {enabled:false, macro_data_type_id: 1, above_below: 'below',threshold:0,description:'Advance Real Retail and Food Services Sales' },
          { enabled:false,macro_data_type_id: 2, above_below: 'below',threshold:0,description:'Industrial Production Index' },
          { enabled:false,macro_data_type_id: 3, above_below: 'below',threshold:0,description:'All Employees, Total Nonfarm/Civilian Labor Force Level' },
          { enabled:false,macro_data_type_id: 4, above_below: 'below',threshold:-10,require_other_macro_data_type_id:5,require_other_enabled:true,description:'Housing Starts' },
          { enabled:false, macro_data_type_id: 5, above_below: 'below',threshold:3,require_other_macro_data_type_id:4,require_other_enabled:true,description:'Real Personal Income' },
          { enabled:false,macro_data_type_id: 6, above_below: 'above',threshold:0,description:'Unemployment Rate Change From Year Ago Seasonally Adjusted' }, */
        {
          enabled: false,
          macro_data_type_id: 'insider',
          above_below: 'above',
          threshold: 1,
          description: 'WhaleWisdom Insider Weekly Sell/Buy Ratio'
        },
        {
          enabled: false,
          macro_data_type_id: 8,
          above_below: 'above',
          threshold: 20,
          description: 'CBOE Volatility Index: VIX'
        },
        {
          enabled: false,
          macro_data_type_id: 'outperformed',
          above_below: 'above',
          threshold: 10,
          days: 20,
          description: 'Index 1 has outperformed Index 2'
        },
        {
          enabled: false,
          macro_data_type_id: 'underperformed',
          above_below: 'above',
          threshold: 10,
          days: 20,
          description: 'Index 1 has underperformed Index 2'
        },
        {
          enabled: false,
          macro_data_type_id: 9,
          above_below: 'above prior reading',
          threshold: 20,
          description: 'Target Fed Funds Rate'
        },
        {
          enabled: false,
          macro_data_type_id: 11,
          above_below: 'above',
          threshold: 125,
          description: 'Buffett Indicator (Market Capitalization over GDP) X 100'
        },
        {
          enabled: false,
          macro_data_type_id: 12,
          above_below: 'above',
          threshold: 4.5,
          description: '10-Year Treasury Rate'
        }
      ],
      spread_macro_types: [
        { macro_data_type_id: 8,  description: 'CBOE VIX' },
        { macro_data_type_id: 9,  description: 'Fed Funds Rate' },
        { macro_data_type_id: 11, description: 'Buffett Indicator' },
        { macro_data_type_id: 12, description: '10-Year Treasury Rate' }
      ],
      perf_days: [
        { value: 20,  text: '20 days (~1 month)' },
        { value: 60,  text: '60 days (~3 months)' },
        { value: 120, text: '120 days (~6 months)' },
        { value: 240, text: '240 days (~1 year)' },
        { value: 480, text: '480 days (~2 years)' }
      ],
      resume_indicators: [
        {
          enabled: true,
          id: 1,
          description: 'When index price goes back above the its defined moving average',
          above_below: '',
          threshold: 0
        },
        {
          enabled: true,
          id: 2,
          description: 'Macro Indicator',
          above_below: 'below',
          threshold: 1
        },
        {
          enabled: false,
          id: 3,
          description: 'Index Price Decline',
          above_below: 'below',
          threshold: 20,
          index: 169819
        },
        {
          enabled: false,
          id: 4,
          description: 'Death Cross (ie. 50 day MA crosses below 200 day MA)',
          threshold: 0
        },
        {
          enabled: false,
          id: 5,
          description: 'Golden Cross (ie. 50 day MA crosses above 200 day MA)',
          threshold: 0
        },
        {
          enabled: false,
          id: 6,
          description: 'Index makes an __ day low',
          threshold: 0,
          day_low: null
        },
        {
          enabled: false,
          id: 7,
          description: 'Index makes an __ day high',
          threshold: 0,
          day_high: null
        },
        {
          enabled: false,
          id: 8,
          description: 'Index % Decline from the Highest High of __ Past Days',
          threshold: 2,
          days_high: null
        },
        {
          enabled: false,
          id: 9,
          description: 'Index Price Increase',
          above_below: 'above',
          threshold: 20,
          index: 169819
        },
        {
          enabled: false,
          id: 10,
          description:
            'Index makes a ___ day high that is at least ___ average true ranges below the ___ day high',
          threshold: 2,
          days_high: null,
          days_high2: null
        },
        {
          enabled: false,
          id: 11,
          description: '% of stocks (entire universe) trading above moving average',
          above_below: 'above',
          threshold: 20
        },
        {
          enabled: false,
          id: 12,
          description: '% of stocks (entire universe) trading below moving average',
          above_below: 'below',
          threshold: 20
        },
        {
          enabled: false,
          id: 13,
          description: 'Portfolio Equity Moving Average Changes',
          threshold: 0
        }
      ],
      days_time_periods: [
        { text: '1 week', value: 7 },
        { text: '2 weeks', value: 14 },
        { text: '3 weeks', value: 21 },
        { text: '4 weeks', value: 28 },
        { text: '50 days', value: 50 },
        { text: '90 days', value: 90 },
        { text: '6-months', value: 180 },
        { text: '1 Year', value: 365 }
      ],
      whale_indexes: [
        { name: 'WhaleIndex 100', whale_index_id: 2 },
        { name: 'WhaleIndex 30 Omega', whale_index_id: 3 },
        { name: 'WhaleIndex 20 Omega', whale_index_id: 5 },
        { name: 'WhaleIndex 30 Conservative', whale_index_id: 4 },
        { name: 'WhaleIndex 20 Conservative', whale_index_id: 6 },
        { name: 'WhaleIndex Mid/Small/Micro 100', whale_index_id: 7 },
        { name: 'WhaleIndex Mid/Small/Micro 30 Omega', whale_index_id: 8 },
        { name: 'WhaleIndex Mid/Small/Micro 20 Omega', whale_index_id: 10 },
        { name: 'WhaleIndex Mid/Small/Micro 30 Conservative', whale_index_id: 9 },
        { name: 'WhaleIndex Mid/Small/Micro 20 Conservative', whale_index_id: 11 },
        { name: 'Whale OMEGA 50 - $1billion MC or greater', whale_index_id: 13 },
        { name: 'WhaleIndex 2.0 - 100', whale_index_id: 16 },
        { name: 'WhaleIndex 2.0 - 30', whale_index_id: 17 },
        { name: 'WhaleIndex 2.0 - 20', whale_index_id: 18 },
        { name: 'WhaleIndex 2.0 Mid/Small/Micro 100', whale_index_id: 19 },
        { name: 'WhaleIndex 2.0 Mid/Small/Micro 30', whale_index_id: 20 },
        { name: 'WhaleIndex 2.0 Mid/Small/Micro 20', whale_index_id: 21 },
        { name: 'WhaleIndex Healthcare - 100', whale_index_id: 22 },
        { name: 'WhaleIndex Healthcare (minus biotech) - 100', whale_index_id: 23 },
        { name: 'WhaleIndex Information Technology - 100', whale_index_id: 24 },
        { name: 'WhaleIndex Energy/Materials - 100', whale_index_id: 41 },
        { name: 'WhaleIndex 200 - <$2B Market Cap', whale_index_id: 38 },
        { name: 'Whale 20 Next Gen', whale_index_id: 42 }
      ],
      above_below: ['above', 'below', 'above a prior reading', 'below a prior reading'],
      mtDialog: false,
      resume_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Ordering',
          align: 'left',
          sortable: true,
          key: 'ordering'
        },
        {
          title: 'Rule',
          align: 'left',
          sortable: true,
          key: 'resume_indicator_id'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      mt_headers: [
        {
          title: 'Enabled',
          align: 'left',
          sortable: true,
          key: 'enabled'
        },
        {
          title: 'Sell Trigger',
          align: 'left',
          sortable: true,
          key: 'sell_trigger'
        },
        {
          title: 'Resume Trigger(s)',
          align: 'left',
          sortable: true,
          key: 'resume_triggers'
        },
        { title: 'Actions', key: 'action', sortable: false }
      ],
      editedIndex: -1,
      editedResumeIndex: -1,
      defaultMt: {
        sell_trigger: null,
        day_low_high_index: null,
        day_low_high: null,
        day_low_high_threshold: 0,
        index_performance: {
          percent_increase: 20,
          days: 200,
          index: 169819,
          compare_to: 'price',
          compare_to_ma: '50'
        },
        moving_average: { days: 200, index: 169819, percent: 70 },
        portfolio_equity: {
          days: 50,
          compare_to: 'is above',
          compare_to_ma: 200,
          percent_increase: 20
        },
        macro_indicator: {
          macro_data_type_id: 1,
          above_below: 'below',
          index1: null,
          index2: null,
          days: 1,
          threshold: 0
        },
        sector_concentration: {
          whale_index_id: null,
          threshold: 20
        },
        stock_perf: { days: 60, threshold: 20, percent_of_stocks: 50 },
        natr: { threshold: 3.0, above_below: 'below' },
        whaleindex_perf: { whale_index_id: null, days: 60, threshold: 25 },
        whaleindex_turnover: { whale_index_id: null, threshold: 40 },
        macro_spread: { macro_data_type_id_1: 12, macro_data_type_id_2: 9, threshold: 0, above_below: 'below' },
        insider_ma: { weeks: 4, threshold: 1.5, above_below: 'above' },
        and_or_indicators: 'or',
        resume_triggers: [],
        enabled: true
      },
      editedMt: {},
      defaultResume: {
        enabled: true,
        resume_indicator_id: null,
        above_below: 'above',
        index: 169819,
        threshold: 0,
        ma1days: 200,
        ma2days: 50,
        day_low: null,
        day_high: null,
        percent: null
      },
      editedResume: {}
    }
  },
  computed: {
    ...mapStoreState('ww', ['sectors']),
    ...mapFields({
      appcriteria: 'app.whaleTraderCriteria',
      current_trading_model: 'app.current_trading_model',
      originalTradingModels: 'user.tradingModels',
      criteria: 'app.whaleTraderCriteria',
      signalView: 'app.signalView'
    }),
    MtTitle() {
      return this.editedIndex === -1 ? 'New Market Timing Rule' : 'Edit Market Timing Rule'
    },
    ResumeTitle() {
      return this.editedResumeIndex === -1
        ? 'Configure New Resume Trading Rule'
        : 'Configure Resume Trading Rule'
    },
    tradingModels() {
      var models = []
      for (var i = 0; i < this.originalTradingModels.length; i++) {
        var m = this.originalTradingModels[i]
        if (m.id != this.current_trading_model.id && m.id > 0) {
          models.push(m)
        }
      }
      return models
    }
  },
  created() {
    this.editedMt = cloneDeep(this.defaultMt)
    this.editedResume = cloneDeep(this.defaultResume)
    for (var i = 2; i < 71; i++) {
      this.ma_days.push(i)
    }
    for (i = 75; i < 301; i += 5) {
      this.ma_days.push(i)
    }
  },

  methods: {
    LookupIndiceName(id) {
      var rec = this.indices.find(element => element.value == id)
      if (rec) {
        return rec.text
      } else {
        return 'not found'
      }
    },
    GetWhaleIndexName(id) {
      var rec = this.whale_indexes.find(element => element.whale_index_id == id)
      if (rec) {
        return rec.name
      } else {
        return 'not found'
      }
    },
    GetMacroIndicatorDescription(id) {
      var rec = this.macro_indicators.find(element => element.macro_data_type_id == id)
      if (rec) {
        return rec.description
      } else {
        return 'not found'
      }
    },
    GetDaysSelect(days) {
      var rec = this.stocks_vs_sp_months.find(element => element.value == days)
      if (rec) {
        return rec.text
      } else {
        return 'not found'
      }
    },
    IsDisabledResumeIndicator(m) {
      var disabled = false
      if (m.id == 1 && !this.criteria.moving_average.enabled) {
        disabled = true
      } else if (
        m.id == 4 &&
        (!this.criteria.index_performance.enabled ||
          this.criteria.index_performance.compare_to != 'moving average')
      ) {
        disabled = true
      }
      return disabled
    },
    edit(item) {
      this.editedIndex = this.criteria.market_timing_rules.indexOf(item)
      this.editedMt = Object.assign(cloneDeep(this.defaultMt), item)
      this.mtDialog = true
    },

    delete_item(item) {
      const index = this.criteria.market_timing_rules.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.criteria.market_timing_rules.splice(index, 1)
    },

    close() {
      this.mtDialog = false
      setTimeout(() => {
        this.editedMt = Object.assign({}, this.defaultMt)
        this.editedIndex = -1
      }, 300)
    },

    save() {
      if (this.editedMt.sell_trigger == 1 && !this.editedMt.moving_average.index) {
        this.editedMt.moving_average.index = 169819
      } else if (this.editedMt.sell_trigger == 2 && !this.editedMt.index_performance.index) {
        this.editedMt.index_performance.index = 169819
      } else if (this.editedMt.sell_trigger == 4 && !this.editedMt.day_low_high_index) {
        this.editedMt.day_low_high_index = 169819
      } else if (this.editedMt.sell_trigger == 5 && !this.editedMt.day_low_high_index) {
        this.editedMt.day_low_high_index = 169819
      }
      if (this.editedIndex > -1) {
        Object.assign(this.criteria.market_timing_rules[this.editedIndex], this.editedMt)
      } else {
        this.criteria.market_timing_rules.push(this.editedMt)
      }
      for (var i = 0; i < this.editedMt.resume_triggers.length; i++) {
        var res = this.editedMt.resume_triggers[i]
        if (res.resume_indicator_id == 1) {
          res.index = this.editedMt.moving_average.index
        }
      }
      this.close()
    },
    closeResumeDialog() {
      this.resumeDialog = false
      setTimeout(() => {
        this.editedResume = Object.assign({}, this.defaultResume)
        this.editedResumeIndex = -1
      }, 300)
    },

    saveResume() {
      if (
        this.editedResume.resume_indicator_id >= 3 &&
        this.editedResume.resume_indicator_id <= 7
      ) {
        if (!this.editedResume.index) {
          this.editedResume.index = 169819
        }
      }
      if (this.editedResume.resume_indicator_id == 1) {
        this.editedResume.index = this.editedMt.moving_average.index
      }
      if (this.editedResumeIndex > -1) {
        Object.assign(this.editedMt.resume_triggers[this.editedResumeIndex], this.editedResume)
      } else {
        this.editedMt.resume_triggers.push(this.editedResume)
      }
      this.closeResumeDialog()
    },
    editResume(item) {
      this.editedResumeIndex = this.editedMt.resume_triggers.indexOf(item)
      this.editedResume = Object.assign({}, item)
      this.resumeDialog = true
    },

    delete_resume(item) {
      const index = this.editedMt.resume_triggers.indexOf(item)
      confirm('Are you sure you want to delete this rule?') &&
        this.editedMt.resume_triggers.splice(index, 1)
    },
    enabled_resume_indicators(sell_trigger_id) {
      var enabled = []
      for (var i = 0; i < this.resume_indicators.length; i++) {
        var indicator = this.resume_indicators[i]
        if (!indicator || (sell_trigger_id != 1 && indicator.id == 1)) {
          continue
        }
        enabled.push(indicator)
      }
      return enabled
    },
    openOrAddResume() {
      if (this.editedResume.resume_indicator_id == 1) {
        this.saveResume()
      } else {
        this.resumeDialog = true
      }
    },
    clear_resumes() {
      if (this.editedMt) {
        this.editedMt.resume_triggers = []
      }
    }
  }
}
</script>
